/* ==========================================================================
   EDME — General Product Carousel
   Kaynaklar:
   - Seçilen koleksiyon
   - Manuel ürün seçimi
   - Son gezilen ürünler
   - İlgili kategori ürünleri
   - Sepete son eklenen ürünler

   Dinamik ürün kartları edme-product-card-renderer section'ı üzerinden
   Liquid ile render edilir.
   ========================================================================== */

(function () {
  'use strict';

  const SELECTOR = '[data-edme-general-product-carousel]';
  const TRACK_SELECTOR = '[data-edme-gpc-track]';
  const PREV_SELECTOR = '[data-edme-gpc-prev]';
  const NEXT_SELECTOR = '[data-edme-gpc-next]';

  const RECENTLY_VIEWED_KEY = 'edme_recently_viewed_products';
  const RECENTLY_CART_KEY = 'edme_recently_cart_products';
  const CART_SNAPSHOT_KEY = 'edme_recently_cart_snapshot';

  const DEFAULT_RENDERER_SECTION_ID = 'edme-product-card-renderer';
  const MAX_STORED_PRODUCTS = 24;
  const CARD_REQUEST_CONCURRENCY = 4;
  const DRAG_THRESHOLD = 12;

  const nativeFetch = window.fetch.bind(window);

  function safeJsonParse(value, fallback) {
    try {
      const parsed = JSON.parse(value);
      return parsed ?? fallback;
    } catch (error) {
      return fallback;
    }
  }

  function getStoredValue(key, fallback) {
    try {
      return safeJsonParse(localStorage.getItem(key), fallback);
    } catch (error) {
      return fallback;
    }
  }

  function setStoredValue(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Gizli mod veya dolu storage durumunda carousel çalışmaya devam eder.
    }
  }

  function getStoredProducts(key) {
    const products = getStoredValue(key, []);
    return Array.isArray(products) ? products : [];
  }

  function normalizeHandle(value) {
    if (!value || typeof value !== 'string') return '';

    const cleanValue = value.split('?')[0].split('#')[0];
    const match = cleanValue.match(/\/products\/([^/]+)/i);

    return match ? decodeURIComponent(match[1]) : cleanValue.replace(/^\/+|\/+$/g, '');
  }

  function sanitizeProduct(product) {
    if (!product) return null;

    const handle = product.handle || normalizeHandle(product.url || '');

    if (!handle) return null;

    return {
      handle,
      url: product.url || `/products/${handle}`,
      timestamp: Date.now()
    };
  }

  function pushUniqueProduct(key, rawProduct, maxLength = MAX_STORED_PRODUCTS) {
    const product = sanitizeProduct(rawProduct);

    if (!product) return false;

    const current = getStoredProducts(key);
    const filtered = current.filter((item) => item && item.handle !== product.handle);
    const next = [product, ...filtered].slice(0, maxLength);

    setStoredValue(key, next);
    return true;
  }

  function pushProductsInOrder(key, products) {
    if (!Array.isArray(products) || !products.length) return false;

    let changed = false;

    // İlk ürün en önde kalacak şekilde tersten eklenir.
    [...products].reverse().forEach((product) => {
      changed = pushUniqueProduct(key, product) || changed;
    });

    return changed;
  }

  function saveCurrentProduct(section) {
    const handle = section.getAttribute('data-current-product-handle');

    if (!handle) return;

    const url = section.getAttribute('data-current-product-url') || `/products/${handle}`;

    pushUniqueProduct(RECENTLY_VIEWED_KEY, { handle, url });
  }

  function getShopifyRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function getCartUrl() {
    return `${getShopifyRoot()}cart.js`;
  }

  function isCartAddUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      return /\/cart\/add(?:\.js)?\/?$/i.test(url.pathname);
    } catch (error) {
      return typeof value === 'string' && value.includes('/cart/add');
    }
  }

  function getRendererSectionId(section) {
    return section.getAttribute('data-renderer-section-id') || DEFAULT_RENDERER_SECTION_ID;
  }

  async function fetchRenderedProductCard(section, handle) {
    if (!handle) return null;

    const rendererSectionId = getRendererSectionId(section);
    const productUrl = `${getShopifyRoot()}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(rendererSectionId)}`;

    try {
      const response = await nativeFetch(productUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      });

      if (!response.ok) return null;

      const html = await response.text();
      const template = document.createElement('template');
      template.innerHTML = html.trim();

      const renderedItem =
        template.content.querySelector('.edme-product-card-renderer__item') ||
        template.content.querySelector('[data-edme-product-card-renderer] .edme-product-card-renderer__item');

      if (!renderedItem) return null;

      const item = document.createElement('div');
      item.className = 'edme-general-product-carousel__item';
      item.innerHTML = renderedItem.innerHTML;

      return item;
    } catch (error) {
      console.error('[EDME General Product Carousel] Product card render error:', error);
      return null;
    }
  }

  async function mapWithConcurrency(items, limit, iterator) {
    const results = new Array(items.length);
    let nextIndex = 0;

    async function worker() {
      while (nextIndex < items.length) {
        const currentIndex = nextIndex;
        nextIndex += 1;
        results[currentIndex] = await iterator(items[currentIndex], currentIndex);
      }
    }

    const workerCount = Math.min(Math.max(limit, 1), items.length);
    await Promise.all(Array.from({ length: workerCount }, worker));

    return results;
  }

  function getTrack(section) {
    return section.querySelector(TRACK_SELECTOR);
  }

  function getPrevBtn(section) {
    return section.querySelector(PREV_SELECTOR);
  }

  function getNextBtn(section) {
    return section.querySelector(NEXT_SELECTOR);
  }

  function getFirstProductVisual(section) {
    const firstItem = section.querySelector('.edme-general-product-carousel__item');

    if (!firstItem) return null;

    return (
      firstItem.querySelector('.edme-pcard__media') ||
      firstItem.querySelector('.edme-pcard__image-wrap') ||
      firstItem.querySelector('picture') ||
      firstItem.querySelector('img')
    );
  }

  function syncArrowPosition(section) {
    const visual = getFirstProductVisual(section);

    if (!visual || window.matchMedia('(max-width: 767px)').matches) {
      section.style.removeProperty('--edme-gpc-arrow-top');
      return;
    }

    const visualHeight = visual.getBoundingClientRect().height;

    if (visualHeight > 0) {
      section.style.setProperty('--edme-gpc-arrow-top', `${Math.round(visualHeight / 2)}px`);
    }
  }

  function getScrollAmount(section, track) {
    const card = track?.querySelector('.edme-general-product-carousel__item');
    const multiplier = parseInt(section.getAttribute('data-scroll-step'), 10) || 2;

    if (!card) return 300 * multiplier;

    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;

    return (card.offsetWidth + gap) * multiplier;
  }

  function updateArrows(section) {
    const track = getTrack(section);
    const prevBtn = getPrevBtn(section);
    const nextBtn = getNextBtn(section);

    if (!track || !prevBtn || !nextBtn) return;

    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const currentLeft = Math.abs(track.scrollLeft);
    const threshold = 4;

    const atStart = currentLeft <= threshold;
    const atEnd = currentLeft >= maxScrollLeft - threshold;
    const noOverflow = maxScrollLeft <= threshold;

    prevBtn.disabled = noOverflow || atStart;
    nextBtn.disabled = noOverflow || atEnd;

    prevBtn.classList.toggle('is-disabled', noOverflow || atStart);
    nextBtn.classList.toggle('is-disabled', noOverflow || atEnd);
  }

  function bindDragScroll(section, track) {
    if (!track || track.dataset.dragBound === 'true') return;

    track.dataset.dragBound = 'true';

    let pointerDown = false;
    let isDragging = false;
    let startX = 0;
    let startLeft = 0;
    let activePointerId = null;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      if (event.target.closest('button, input, select, textarea')) return;

      pointerDown = true;
      isDragging = false;
      startX = event.clientX;
      startLeft = track.scrollLeft;
      activePointerId = event.pointerId;
    });

    track.addEventListener('pointermove', (event) => {
      if (!pointerDown) return;

      const difference = event.clientX - startX;

      if (!isDragging && Math.abs(difference) < DRAG_THRESHOLD) return;

      if (!isDragging) {
        isDragging = true;
        track.classList.add('is-dragging');
        track.setPointerCapture?.(activePointerId);
      }

      event.preventDefault();
      track.scrollLeft = startLeft - difference;
    });

    const endDrag = (event) => {
      if (!pointerDown) return;

      pointerDown = false;

      if (isDragging) {
        track.dataset.wasDragged = 'true';
        window.setTimeout(() => delete track.dataset.wasDragged, 0);
      }

      isDragging = false;
      activePointerId = null;
      track.classList.remove('is-dragging');

      try {
        track.releasePointerCapture?.(event.pointerId);
      } catch (error) {
        // Pointer zaten serbest bırakılmış olabilir.
      }

      window.requestAnimationFrame(() => {
        updateArrows(section);
        syncArrowPosition(section);
      });
    };

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', endDrag);

    track.addEventListener(
      'click',
      (event) => {
        if (track.dataset.wasDragged === 'true') {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  }

  function bindTrack(section, track) {
    if (!track || track.dataset.scrollBound === 'true') return;

    track.dataset.scrollBound = 'true';
    bindDragScroll(section, track);

    track.addEventListener(
      'scroll',
      () => window.requestAnimationFrame(() => updateArrows(section)),
      { passive: true }
    );
  }

  function observeImages(section) {
    section.querySelectorAll('.edme-general-product-carousel__item img').forEach((image) => {
      if (image.complete) {
        syncArrowPosition(section);
        return;
      }

      image.addEventListener('load', () => syncArrowPosition(section), { once: true });
    });
  }

  async function renderProductHandles(section, rawHandles) {
    const track = getTrack(section);
    const emptyText = section.getAttribute('data-empty-text') || 'Bu alan için henüz ürün bulunamadı.';

    if (!track) return;

    const handles = [...new Set(rawHandles.filter(Boolean))];
    track.innerHTML = '';

    if (!handles.length) {
      track.innerHTML = `<div class="edme-general-product-carousel__empty">${emptyText}</div>`;
      updateArrows(section);
      return;
    }

    const cards = await mapWithConcurrency(
      handles,
      CARD_REQUEST_CONCURRENCY,
      (handle) => fetchRenderedProductCard(section, handle)
    );

    const validCards = cards.filter(Boolean);

    if (!validCards.length) {
      track.innerHTML = `<div class="edme-general-product-carousel__empty">${emptyText}</div>`;
      updateArrows(section);
      return;
    }

    const fragment = document.createDocumentFragment();
    validCards.forEach((card) => fragment.appendChild(card));
    track.appendChild(fragment);

    document.dispatchEvent(
      new CustomEvent('edme:product-cards-rendered', {
        detail: { section, container: track }
      })
    );

    observeImages(section);

    [0, 250, 700].forEach((delay) => {
      window.setTimeout(() => {
        updateArrows(section);
        syncArrowPosition(section);
      }, delay);
    });
  }

  async function hydrateRecentlyViewed(section) {
    const limit = parseInt(section.getAttribute('data-product-limit'), 10) || 12;
    const currentHandle = section.getAttribute('data-current-product-handle') || '';

    const handles = getStoredProducts(RECENTLY_VIEWED_KEY)
      .filter((item) => item?.handle && item.handle !== currentHandle)
      .map((item) => item.handle)
      .slice(0, limit);

    await renderProductHandles(section, handles);
  }

  function createCartSnapshot(items) {
    return (items || [])
      .filter((item) => item?.handle)
      .map((item) => ({
        key: String(item.key || item.variant_id || item.id || item.handle),
        handle: item.handle,
        url: item.url || `/products/${item.handle}`,
        quantity: Number(item.quantity || 0)
      }));
  }

  function updateCartHistoryFromItems(items) {
    const currentSnapshot = createCartSnapshot(items);
    const previousSnapshot = getStoredValue(CART_SNAPSHOT_KEY, []);
    const previousItems = Array.isArray(previousSnapshot) ? previousSnapshot : [];
    const previousByKey = new Map(previousItems.map((item) => [String(item.key), Number(item.quantity || 0)]));

    let newlyAdded = currentSnapshot.filter((item) => {
      const previousQuantity = previousByKey.get(String(item.key)) || 0;
      return item.quantity > previousQuantity;
    });

    // İlk çalışmada geçmiş boşsa mevcut sepet, başlangıç geçmişi olarak kullanılır.
    if (!previousItems.length && !getStoredProducts(RECENTLY_CART_KEY).length) {
      newlyAdded = currentSnapshot;
    }

    if (newlyAdded.length) {
      pushProductsInOrder(RECENTLY_CART_KEY, newlyAdded);
    }

    setStoredValue(CART_SNAPSHOT_KEY, currentSnapshot);
    return newlyAdded.length > 0;
  }

  async function syncCartHistory() {
    try {
      const response = await nativeFetch(getCartUrl(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) return false;

      const cart = await response.json();
      return updateCartHistoryFromItems(cart.items || []);
    } catch (error) {
      return false;
    }
  }

  async function hydrateRecentlyCart(section, syncFirst = true) {
    const limit = parseInt(section.getAttribute('data-product-limit'), 10) || 12;

    if (syncFirst) {
      await syncCartHistory();
    }

    const handles = getStoredProducts(RECENTLY_CART_KEY)
      .filter((item) => item?.handle)
      .map((item) => item.handle)
      .slice(0, limit);

    await renderProductHandles(section, handles);
  }

  function refreshRecentlyCartSections(syncFirst = false) {
    document.querySelectorAll(`${SELECTOR}[data-source="recently_cart"]`).forEach((section) => {
      hydrateRecentlyCart(section, syncFirst);
    });
  }

  function recordCartAddPayload(payload) {
    const rawItems = Array.isArray(payload?.items) ? payload.items : [payload];
    const products = rawItems
      .map((item) => ({
        handle: item?.handle || normalizeHandle(item?.url || ''),
        url: item?.url
      }))
      .filter((item) => item.handle);

    if (products.length) {
      pushProductsInOrder(RECENTLY_CART_KEY, products);
      refreshRecentlyCartSections(false);
    }

    window.setTimeout(async () => {
      await syncCartHistory();
      refreshRecentlyCartSections(false);
    }, 250);
  }

  function installFetchCartWatcher() {
    if (window.__edmeGpcFetchWatcherInstalled) return;
    window.__edmeGpcFetchWatcherInstalled = true;

    window.fetch = async function (...args) {
      const response = await nativeFetch(...args);
      const requestUrl = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

      if (response.ok && isCartAddUrl(requestUrl)) {
        response
          .clone()
          .json()
          .then(recordCartAddPayload)
          .catch(() => {
            window.setTimeout(async () => {
              await syncCartHistory();
              refreshRecentlyCartSections(false);
            }, 250);
          });
      }

      return response;
    };
  }

  function installXhrCartWatcher() {
    if (window.__edmeGpcXhrWatcherInstalled || !window.XMLHttpRequest) return;
    window.__edmeGpcXhrWatcherInstalled = true;

    const nativeOpen = XMLHttpRequest.prototype.open;
    const nativeSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__edmeGpcUrl = url;
      return nativeOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (...args) {
      if (isCartAddUrl(this.__edmeGpcUrl || '')) {
        this.addEventListener('load', () => {
          if (this.status < 200 || this.status >= 300) return;

          try {
            recordCartAddPayload(safeJsonParse(this.responseText, {}));
          } catch (error) {
            window.setTimeout(async () => {
              await syncCartHistory();
              refreshRecentlyCartSections(false);
            }, 250);
          }
        });
      }

      return nativeSend.apply(this, args);
    };
  }

  function installCartEventWatchers() {
    if (window.__edmeGpcCartEventsInstalled) return;
    window.__edmeGpcCartEventsInstalled = true;

    const events = [
      'cart:updated',
      'cart:change',
      'cart:refresh',
      'cart:item-added',
      'product:added',
      'ajaxProduct:added',
      'theme:cart:change'
    ];

    events.forEach((eventName) => {
      document.addEventListener(eventName, () => {
        window.setTimeout(async () => {
          await syncCartHistory();
          refreshRecentlyCartSections(false);
        }, 250);
      });
    });
  }

  function installCartTracking() {
    installFetchCartWatcher();
    installXhrCartWatcher();
    installCartEventWatchers();
  }

  function bindArrows(section) {
    const prevBtn = getPrevBtn(section);
    const nextBtn = getNextBtn(section);

    if (prevBtn && prevBtn.dataset.clickBound !== 'true') {
      prevBtn.dataset.clickBound = 'true';
      prevBtn.addEventListener('click', () => {
        const track = getTrack(section);
        if (!track) return;

        track.scrollBy({
          left: -getScrollAmount(section, track),
          behavior: 'smooth'
        });
      });
    }

    if (nextBtn && nextBtn.dataset.clickBound !== 'true') {
      nextBtn.dataset.clickBound = 'true';
      nextBtn.addEventListener('click', () => {
        const track = getTrack(section);
        if (!track) return;

        track.scrollBy({
          left: getScrollAmount(section, track),
          behavior: 'smooth'
        });
      });
    }
  }

  function bindResize(section) {
    if (section.dataset.resizeBound === 'true') return;
    section.dataset.resizeBound = 'true';

    window.addEventListener(
      'resize',
      () => {
        window.requestAnimationFrame(() => {
          updateArrows(section);
          syncArrowPosition(section);
        });
      },
      { passive: true }
    );
  }

  function initSection(section) {
    if (!section || section.dataset.edmeGeneralProductCarouselInit === 'true') return;

    section.dataset.edmeGeneralProductCarouselInit = 'true';

    const source = section.getAttribute('data-source');
    const track = getTrack(section);

    saveCurrentProduct(section);
    bindTrack(section, track);
    bindArrows(section);
    bindResize(section);
    observeImages(section);

    if (source === 'recently_viewed') {
      hydrateRecentlyViewed(section);
    } else if (source === 'recently_cart') {
      hydrateRecentlyCart(section, true);
    }

    [0, 300, 900].forEach((delay) => {
      window.setTimeout(() => {
        updateArrows(section);
        syncArrowPosition(section);
      }, delay);
    });
  }

  function initAll() {
    installCartTracking();
    document.querySelectorAll(SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.matches?.(SELECTOR)
      ? event.target
      : event.target.querySelector?.(SELECTOR);

    if (section) {
      section.dataset.edmeGeneralProductCarouselInit = 'false';
      initSection(section);
    }
  });
})();
